import { getAuth } from 'firebase/auth';
import type { UserHealthInsights } from '@researcher/shared';
import { apiUrl } from '../config/api';

async function getAuthHeaders(): Promise<HeadersInit> {
  const headers: HeadersInit = { 'Content-Type': 'application/json' };

  const user = getAuth().currentUser;
  if (user) {
    const idToken = await user.getIdToken();
    headers['Authorization'] = `Bearer ${idToken}`;
  }

  return headers;
}

// Client-side cache — avoids re-fetching on every page navigation
let cachedInsights: UserHealthInsights | null = null;
let cacheTimestamp = 0;
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

export async function fetchHealthInsights(): Promise<UserHealthInsights | null> {
  if (cachedInsights && Date.now() - cacheTimestamp < CACHE_TTL) {
    return cachedInsights;
  }

  const headers = await getAuthHeaders();
  const response = await fetch(apiUrl('/health/insights'), { headers });
  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'Failed to fetch health insights');
  }

  cachedInsights = data.data || null;
  cacheTimestamp = Date.now();
  return cachedInsights;
}

export async function refreshHealthInsights(): Promise<UserHealthInsights | null> {
  const headers = await getAuthHeaders();
  const response = await fetch(apiUrl('/health/insights/refresh'), {
    method: 'POST',
    headers,
  });
  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'Failed to refresh health insights');
  }

  cachedInsights = data.data || null;
  cacheTimestamp = Date.now();
  return cachedInsights;
}
